import { useEffect, useRef, useState } from "react";
import {
  Button,
  Chip,
  Group,
  Text,
  Loader,
  Accordion,
  Collapse,
  Select,
  Stack,
  Slider,
  Switch,
  TextInput,
  Textarea,
  Alert,
} from "@mantine/core";
import {
  getGenerationOptions,
  generateImage,
  getCustomModels,
  expandPrompt,
  createFluxImg2Img,
} from "../api";
import { IconCoins, IconWand, IconClipboardList } from "@tabler/icons-react";
import mixpanel from "../tracking";
import { notifications } from "@mantine/notifications";
import { useStore } from "../store";
import { Link } from "wouter";
import { useForm } from "@mantine/form";
import V2PluginRunForm from "./V2PluginRunForm.jsx";
import SubjectBuilder from "./SubjectBuilder.jsx";
import { composeSubject } from "./subjectBuilderCatalog.js";
import { usePluginPicker } from "./usePluginPicker";
import { imageThumbUri } from "./imageUris";

function isIterable(obj) {
  // checks for null and undefined
  if (obj == null) {
    return false;
  }
  return typeof obj[Symbol.iterator] === "function";
}

export default function GeneratorOptions() {
  const [, setConfig] = useState({});
  const jobs = useStore((state) => state.jobs);
  const setJobs = useStore((state) => state.setJobs);
  const galleryImages = useStore((state) => state.galleryImages);
  const profile = useStore((state) => state.profile);
  const selectedImages = useStore((state) => state.selectedImages);
  // Image-domain plugins only, matching the current selection: image-input
  // (I2I) plugins with a selection, text-only (T2I) plugins without.
  const {
    optionSource,
    setOptionSource,
    compatiblePlugins,
    clearedLabel,
  } = usePluginPicker("pv-last-plugin-image", false);
  const pluginOptions = compatiblePlugins.map((p) => ({
    label: p.pluginTitle,
    value: p.id,
  }));
  const [options, setOptions] = useState([]);
  const [selectedCustomModel, setSelectedCustomModel] = useState(null);
  const [allCustomModels, setAllCustomModels] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [i2iPrompt, setI2iPrompt] = useState("");

  const [filters, setFilters] = useState({ environment: "", action: "" });

  const generatorConfig = useStore((state) => state.generatorConfig);

  const form = useForm({
    // TBD: generatorCOnfig might contain arrays, which forces the chips to become multi-select... That is BAD...
    initialValues: generatorConfig || {
      aspectRatio: "portrait",
      creativity: 3.5,
      numImages: 1,
      seed: "",
      config: {},
      styleTemplateId: "",
    },
    validate: {
      seed: (value) =>
        value === "" || /^[0-9]+$/.test(value) ? null : "Seed must be a number",
    },
  });

  const [builderOpen, setBuilderOpen] = useState(false);
  const builderPrefixRef = useRef("");

  const getOptionKey = (option) => option.id || option.title;
  const getValueKey = (value) => value.id || value.titleUser;

  const calculateThreshold = (config) => {
    let thresholdCount = 0;
    if (selectedCustomModel) thresholdCount += 1;

    for (var key in config) {
      let selVal = config[key];
      let relevantOption = options?.options?.find(
        (o) => getOptionKey(o) === key
      );
      if (!relevantOption || !relevantOption.values) continue;
      if (!isIterable(selVal)) continue;

      for (var configuredValue of selVal) {
        let selectedValue = relevantOption.values.find(
          (v) => getValueKey(v) === configuredValue
        );
        if (selectedValue) thresholdCount += selectedValue.threshold;
      }
    }

    return thresholdCount;
  };

  const threshold = calculateThreshold(form.values.config);
  const stylesConflicts = threshold > 4;

  useEffect(() => {
    if (generatorConfig) {
      console.debug("Generator config changed, setting", generatorConfig);
      form.setValues(generatorConfig);
      form.setInitialValues(generatorConfig);
    }
    // form is intentionally not a dep: its methods are stable, and an unstable
    // identity here would refetch/reapply on every render (infinite loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatorConfig]);

  useEffect(() => {
    getCustomModels(optionSource).then((data) => {
      setAllCustomModels(data);
    });
    setSelectedCustomModel(null);
  }, [optionSource]);

  // Engine family is derived from the chosen custom model (or Flux by
  // default). Users never pick Flux/Krea by name.
  const selectedCustom = allCustomModels.find(
    (p) => p.id === selectedCustomModel
  );
  const baseModel = selectedCustom?.baseModelFamily || "flux1";

  const customModels = allCustomModels
    .filter((p) => p.baseModelFamily !== "krea2")
    .map((p) => ({
      label: p.name || p.id,
      value: p.id,
    }));

  useEffect(() => {
    getGenerationOptions(optionSource).then((data) => {
      setOptions(data);
      let conf = {};
      for (let element of data.options) {
        conf[getOptionKey(element)] = [];
      }
      setConfig(conf);
      // Apply manifest defaults if present
      if (data.defaults) {
        const defaults = {};
        if (data.defaults.creativity != null)
          defaults.creativity = data.defaults.creativity;
        if (data.defaults.aspectRatio != null)
          defaults.aspectRatio = data.defaults.aspectRatio;
        if (Object.keys(defaults).length > 0)
          form.setValues((prev) => ({ ...prev, ...defaults }));
      }
      try {
        mixpanel.track("Generation options loaded");
      } catch {}
    });
    // form is intentionally not a dep (see effect above): including it made
    // every render retrigger this fetch, looping against /generation/options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionSource]);

  const styles = options?.presets?.flatMap((p) => p.values);

  const handleChipClick = (option) => () => {
    const keyName = getOptionKey(option);
    form.setFieldValue("config." + keyName, null);
  };

  const handleExpandPrompt = async () => {
    const current = form.values.prompt;
    if (!current || !current.trim()) {
      notifications.show({
        title: "Nothing to expand",
        message: "Type a short prompt first, then click Expand.",
        color: "yellow",
      });
      return;
    }
    setIsExpanding(true);
    try {
      const { expandedPrompt } = await expandPrompt(optionSource, current);
      form.setFieldValue("prompt", expandedPrompt);
      try {
        mixpanel.track("Prompt Expanded", { inputLength: current.length });
      } catch {}
    } catch (error) {
      notifications.show({
        title: "Couldn't expand prompt",
        message: error.message || "The prompt expansion service is unavailable.",
        color: "red",
      });
    } finally {
      setIsExpanding(false);
    }
  };

  // v2 plugins drive a different submission path: they declare typed inputs
  // (text/choice/image), produce N outputs into a Bundle, and (for image-input
  // ones) route through nano-banana — which is incompatible with picking a
  // custom-trained LoRA. So when v2 is selected, we suppress the entire legacy
  // form (LoRA dropdown, prompt textarea, chip options) and render the
  // recipe-driven form instead.
  const isV2Plugin = options?.isV2;

  // Plugin selector lives at the top of every render so it doesn't jump
  // around when a plugin is picked (a v2 plugin used to swap the form's
  // contents out from under it, which moved the selector to a different
  // vertical position).
  const pluginSelector = (
    <>
      <Select
        label="Plugin"
        placeholder={
          pluginOptions.length === 0
            ? selectedImages.length > 0
              ? "No image plugins for editing images"
              : "No plugins installed"
            : undefined
        }
        disabled={pluginOptions.length === 0}
        onChange={setOptionSource}
        value={optionSource}
        clearable
        data={pluginOptions}
      />
      {clearedLabel && (
        <Text c="dim" fz="xs">
          {`"${clearedLabel}" doesn't apply to the current selection.`}
        </Text>
      )}
    </>
  );

  // Image-to-image: when the user has selected images in the gallery (and no
  // plugin), the selected images ARE the input. The subject lives in the
  // picture, so the chip options are hidden and the prompt describes the
  // target scene instead.
  if (!optionSource && selectedImages.length > 0) {
    const submitI2I = () => {
      if (!i2iPrompt.trim()) return;
      setIsSubmitting(true);
      // FLUX.2 dev edit, not nano-banana: the latter's upstream safety checker
      // refuses content this engine would accept; FLUX.2 has none enabled.
      // Single image — the first selection is the edit target.
      createFluxImg2Img({
        prompt: i2iPrompt,
        inputImageIds: selectedImages.slice(0, 1),
      })
        .then((data) => {
          const newJob = {
            // Show the edit source behind the processing card.
            uri: imageThumbUri(galleryImages, selectedImages[0]),
            clientId: Math.random().toString(36).substr(2, 9),
            ...data,
          };
          setJobs([newJob, ...jobs]);
          try {
            mixpanel.track("Generation Start", { mode: "I2I" });
          } catch {}
          setIsSubmitting(false);
        })
        .catch((error) => {
          notifications.show({
            title: "An error occurred!",
            message: error.message,
            color: "red",
          });
          setIsSubmitting(false);
        });
    };

    return (
      <Stack p="xs" gap="xs">
        {pluginSelector}
        <Textarea
          label="Prompt"
          value={i2iPrompt}
          onChange={(evt) => setI2iPrompt(evt.currentTarget.value)}
          minRows={3}
          maxRows={6}
          autosize
          placeholder="e.g. Put her on a beach at sunset"
        />
        <Button
          variant="light"
          loading={isSubmitting}
          disabled={!i2iPrompt.trim()}
          onClick={submitI2I}
        >
          Create
        </Button>
      </Stack>
    );
  }

  if (isV2Plugin) {
    return (
      <Stack p="xs" gap="xs">
        {pluginSelector}
        <V2PluginRunForm pluginId={optionSource} options={options} />
      </Stack>
    );
  }

  return (
    <Stack p="xs" gap="xs">
      {pluginSelector}
      <Select
        description={
          profile?.plan === "Free" ? (
            <>
              You need to{" "}
              <Link to="/payment/plans">upgrade to create custom models</Link>{" "}
              of people or characters
            </>
          ) : (
            <>
              Select one of your <Link to="/training-datasets">AI models</Link>
            </>
          )
        }
        label="Model"
        clearable
        disabled={profile?.plan === "Free" || customModels.length === 0}
        onChange={setSelectedCustomModel}
        value={selectedCustomModel}
        data={customModels}
      />

      <form
        onSubmit={form.onSubmit((values) => {
          setIsSubmitting(true);
          var dtoConfig = {};
          for (var x in values.config) {
            const val = values.config[x];
            // check if val is an array:
            if (Array.isArray(val)) dtoConfig[x] = val;
            else dtoConfig[x] = [val];
            // if (isIterable(val)) dtoConfig[x] = [...val];
            // shouldn't happen, but it does...
            // else
            // dtoConfig[x] = val;
          }
          const dto = {
            sourceId: optionSource,
            customModelId: selectedCustomModel,
            baseModel: baseModel === "flux1" ? null : baseModel,
            seed: values.seed === "" ? null : values.seed,
            prompt: values.prompt,
            numImages: values.numImages,
            creativity: values.creativity,
            config: dtoConfig,
            aspectRatio: values.aspectRatio,
            styletemplateId: values.styleTemplateId,
          };
          generateImage(dto)
            .then((data) => {
              let newJob = {
                config: dtoConfig,
                clientId: Math.random().toString(36).substr(2, 9),
                ...data,
              };
              setJobs([newJob, ...jobs]);
              // let newCard = { jobId: data.jobId, config: dtoConfig };
              // setCards([newCard, ...cards]);
              try {
                mixpanel.track("Generation Start");
              } catch {}
              setIsSubmitting(false);
            })
            .catch((error) => {
              notifications.show({
                title: "An error occurred!",
                message: error.message,
                color: "red",
              });
              setIsSubmitting(false);
            });
        })}
      >
        <Stack gap="sm">
          <Collapse in={builderOpen}>
            <SubjectBuilder
              onChange={(sels) => {
                // Live prefix-replace: the composed fragment always sits at the
                // top of the prompt, replacing its previous incarnation — so
                // re-picking a chip never duplicates and manual edits below
                // the prefix survive.
                const built = composeSubject(sels);
                const cur = form.values.prompt || "";
                const prev = builderPrefixRef.current;
                const rest =
                  prev && cur.startsWith(prev) ? cur.slice(prev.length) : cur;
                builderPrefixRef.current = built;
                form.setFieldValue(
                  "prompt",
                  built ? `${built},${rest}` : rest.replace(/^,\s*/, "")
                );
              }}
            />
          </Collapse>
          <Group justify="flex-start">
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={
                <IconClipboardList size={14} stroke={1.5} />
              }
              onClick={() => setBuilderOpen((o) => !o)}
            >
              {builderOpen ? "Hide subject builder" : "Build subject"}
            </Button>
          </Group>
          <Textarea
            label="Prompt"
            key={form.key("prompt")}
            {...form.getInputProps(`prompt`)}
            minRows={3}
            maxRows={6}
            autosize
          />

          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              size="compact-xs"
              onClick={handleExpandPrompt}
              loading={isExpanding}
              leftSection={<IconWand size={14} stroke={1.5} />}
              rightSection={
                <Group gap={2} wrap="nowrap">
                  <IconCoins stroke={1.5} size={11} /> 1
                </Group>
              }
            >
              Expand with AI
            </Button>
          </Group>

          {styles?.length > 0 && (
            <Select
              key={form.key("styleTemplateId")}
              label="Style"
              clearable
              searchable
              data={styles.map((option) => ({
                label: option.titleUser,
                value: option.id,
              }))}
              {...form.getInputProps(`styleTemplateId`)}
            />
          )}

          <div>
            <Text fz="sm">Aspect Ratio</Text>
            <Chip.Group
              size="xs"
              {...form.getInputProps(`aspectRatio`)}
              key={form.key("aspectRatio")}
            >
              <Group gap="xs">
                <Chip variant="light" size="xs" value="portrait">
                  Portrait
                </Chip>
                <Chip variant="light" size="xs" value="landscape">
                  Landscape
                </Chip>
                <Chip variant="light" size="xs" value="square">
                  Square
                </Chip>
              </Group>
            </Chip.Group>
          </div>

          <Slider
            label="Creativity"
            {...form.getInputProps(`creativity`)}
            key={form.key("creativity")}
            color="blue"
            mt="sm"
            mb="md"
            defaultValue={3.5}
            min={3.0}
            max={7.0}
            step={0.1}
            marks={[
              { value: 3.5, label: "Adhering" },
              { value: 6.5, label: "Creative" },
            ]}
          />

          <TextInput
            {...form.getInputProps(`seed`)}
            key={form.key("seed")}
            label="Seed"
            description="Same seed generates similar images"
          />

          {/* <NumberInput
            {...form.getInputProps(`numImages`)}
            key={form.key("numImages")}
            disabled
            min={1}
            max={1}
            label="Number of Images"
            description="Generate images in parallel (soon)"
          /> */}
        </Stack>
        <Accordion>
          {!options && <Loader />}

          {options &&
            options.options &&
            options.options
              .sort(function (b, a) {
                var x = a.title.toLowerCase();
                var y = b.title.toLowerCase();
                return x < y ? -1 : x > y ? 1 : 0;
              })
              .map((option) => {
                const optKey = getOptionKey(option);
                return (
                  <Accordion.Item key={optKey} value={optKey}>
                    <Accordion.Control>{option.title}</Accordion.Control>
                    <Accordion.Panel>
                      {option.type === "toggle" ? (
                        <Switch
                          label={option.values[0]?.titleUser}
                          {...form.getInputProps(`config.${optKey}`, {
                            type: "checkbox",
                          })}
                        />
                      ) : (
                        <>
                          {option.values.length > 10 && (
                            <TextInput
                              size="xs"
                              mb="xs"
                              value={filters[optKey]}
                              onChange={(evt) =>
                                setFilters({
                                  ...filters,
                                  [optKey]: evt.target.value,
                                })
                              }
                            />
                          )}
                          <Chip.Group
                            multiple={option.allowMulti}
                            {...form.getInputProps(`config.${optKey}`)}
                          >
                            <Group gap="xs">
                              {option.values
                                .filter((value) => {
                                  if (!filters[optKey]) return true;
                                  return value.titleUser
                                    .toLowerCase()
                                    .includes(filters[optKey].toLowerCase());
                                })
                                .sort(function (a, b) {
                                  var x = a.titleUser.toLowerCase();
                                  var y = b.titleUser.toLowerCase();
                                  return x < y ? -1 : x > y ? 1 : 0;
                                })
                                .map((value) => (
                                  <Chip
                                    key={`${optKey}-${getValueKey(value)}`}
                                    variant="light"
                                    onClick={handleChipClick(option)}
                                    size="xs"
                                    value={getValueKey(value)}
                                  >
                                    {value.previewImageUrl && (
                                      <img
                                        src={value.previewImageUrl}
                                        alt=""
                                        style={{
                                          width: 18,
                                          height: 18,
                                          objectFit: "cover",
                                          borderRadius: 3,
                                          marginRight: 6,
                                          verticalAlign: "middle",
                                          display: "inline-block",
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.style.display = "none";
                                        }}
                                      />
                                    )}
                                    {value.titleUser}
                                  </Chip>
                                ))}
                            </Group>
                          </Chip.Group>
                        </>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
        </Accordion>

        {stylesConflicts && (
          <Alert color="red">Too many conflicting options configured</Alert>
        )}

        <Button
          variant="light"
          rightSection={
            <Group gap={4}>
              <IconCoins stroke={1.5} size="15" />{" "}
              {30 * (threshold > 2 ? 2 : 1)}
            </Group>
          }
          mt="md"
          type="submit"
          disabled={stylesConflicts}
          loading={isSubmitting}
        >
          Create
        </Button>
      </form>
    </Stack>
  );
}
